import { HeroIcon, type HeroIconName } from '@codeware/shared/ui/primitives';
import { Button } from '@codeware/shared/ui/shadcn/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '@codeware/shared/ui/shadcn/components/card';
import type { CardBlock as CardBlockProps } from '@codeware/shared/util/payload-types';
import { type TailwindColor, tailwind } from '@codeware/shared/util/tailwind';
import { cn } from '@codeware/shared/util/ui';
import { ExternalLinkIcon, LinkIcon } from 'lucide-react';

import { useColumnSize } from '../../providers/ColumnSizeProvider';
import { usePayload } from '../../providers/PayloadProvider';
import { interactiveSurface } from '../../utils/interactive-surface';
import { handleAsRoute } from '../../utils/internal-link';
import { resolveCardBlockLink } from '../../utils/resolve-card-block-link';

/**
 * Render Payload card block cards in a responsive grid,
 * with a subtle hover effect and modern styling.
 */
export const CardBlock: React.FC<CardBlockProps> = ({ cards }) => {
  const { navigate } = usePayload();
  const { effectiveFraction = 1 } = useColumnSize({ silent: true }) ?? {};

  if (!cards?.length) {
    return null;
  }

  // Number of columns should not exceed the number of cards
  // or be more than will fit in the container
  const maxColumns = Math.min(
    cards.length,
    effectiveFraction <= 0.5 ? 1 : effectiveFraction <= 0.75 ? 2 : 3
  );

  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-6',
        { 'sm:grid-cols-2': maxColumns > 1 },
        { 'lg:grid-cols-3 lg:gap-8': maxColumns === 3 }
      )}
    >
      {cards.map((card, index) => {
        const { title, description, content, enableLink, link, brand } = card;
        const { icon, color } = brand ?? {};

        const hasHeader = title || description || icon;
        const linkDetails = enableLink ? resolveCardBlockLink(link) : null;
        const cardLink =
          linkDetails?.navTrigger === 'card' ? linkDetails : null;

        // A real link stretched over the card: reachable by keyboard, opened
        // in a new tab with a modified click, and named by the card's title.
        // Links inside the card's content sit above it and stay clickable
        const stretchedLink = (children: React.ReactNode, name?: string) =>
          cardLink && (
            <a
              href={cardLink.url}
              {...(cardLink.newTab && {
                target: '_blank',
                rel: 'noreferrer'
              })}
              aria-label={name}
              onClick={(e) => {
                if (cardLink.newTab || !handleAsRoute(e)) return;
                navigate(cardLink.url);
              }}
              className="after:absolute after:inset-0 after:rounded-lg focus-visible:outline-hidden"
            >
              {children}
            </a>
          );

        return (
          <Card
            key={index}
            className={cn(
              'text-card-foreground relative overflow-hidden rounded-lg',
              // Only a card that is itself the link answers the pointer; one
              // linking from its footer leaves that to the footer button.
              // `group` with it, so the icon stays still on the rest
              cardLink ? ['group', interactiveSurface()] : 'bg-card/50 border',
              {
                // Limit card width when a single column have more than half the page width
                'max-w-sm': maxColumns === 1 && effectiveFraction > 0.5
              }
            )}
          >
            {hasHeader && (
              <CardHeader>
                {icon && (
                  <div
                    // Workaround to prevent all colors to be bundled just-in-case:
                    // Tailwind can not resolve dynamic colors that are not pre-defined
                    // so we need to use inline styles to set the border color.
                    style={{
                      borderColor: tailwind.colorMaybe(color)
                    }}
                    // The link colour on a tint of itself, as feature cards draw
                    // theirs: `primary-foreground` is the text *on* a primary
                    // button, dark in dark mode, and left the icon unseen
                    className={cn(
                      'bg-core-link/10 flex size-14 items-center justify-center rounded-full border transition-[border-color,box-shadow] duration-200 ease-out group-hover:shadow-md',
                      !color &&
                        'border-core-link/20 group-hover:border-core-link/50'
                    )}
                  >
                    <HeroIcon
                      icon={icon as HeroIconName}
                      color={color as TailwindColor}
                      className={cn(
                        'size-7 transition-transform duration-200 ease-out motion-safe:group-hover:scale-110',
                        !color && 'text-core-link'
                      )}
                    />
                  </div>
                )}
                {title && (
                  <CardTitle className="text-core-headline mt-4 text-xl font-semibold">
                    {stretchedLink(title) ?? title}
                  </CardTitle>
                )}
                {description && (
                  <CardDescription className="text-sm">
                    {description}
                  </CardDescription>
                )}
              </CardHeader>
            )}

            {/* Without a title the link has no text of its own to carry */}
            {!title &&
              stretchedLink(null, description ?? cardLink?.url ?? undefined)}

            {/* Everything in the content a visitor can use rises above the
                stretched link, not only links: a copy button or a video
                control would otherwise follow the card instead */}
            <CardContent className="[&_:is(a,button,input,select,textarea,summary,video,audio,iframe,[tabindex])]:relative [&_:is(a,button,input,select,textarea,summary,video,audio,iframe,[tabindex])]:z-10">
              {content}
            </CardContent>

            {/* Add the link to card footer */}
            {linkDetails && linkDetails.navTrigger === 'link' && (
              <CardFooter className="mt-auto">
                <Button
                  variant="link"
                  className="text-secondary-foreground group-hover:text-core-link flex h-auto items-center gap-2 p-0 text-sm transition-all duration-300 hover:cursor-pointer"
                  onClick={() => navigate(linkDetails.url, linkDetails.newTab)}
                >
                  {(linkDetails.newTab && (
                    <ExternalLinkIcon className="h-4 w-4" />
                  )) || <LinkIcon className="h-4 w-4" />}
                  <span>{linkDetails.label}</span>
                </Button>
              </CardFooter>
            )}
          </Card>
        );
      })}
    </div>
  );
};
